// src/tools/registry.tsx
// Single source of truth for the developer tools: metadata + lazy-loaded component.
import { lazy } from 'react';
import type { IconType } from 'react-icons';
import {
  FaExchangeAlt,
  FaLock,
  FaColumns,
  FaAsterisk,
  FaAlignLeft,
  FaParagraph,
  FaPalette,
  FaFingerprint,
  FaSuperscript,
  FaMarkdown,
  FaFileExcel,
} from 'react-icons/fa';

export type ToolCategory = 'tools' | 'custom';

export const CATEGORY_LABELS: Record<ToolCategory, string> = {
  tools: 'Tools',
  custom: 'Sur mesure',
};

export interface ToolMeta {
  slug: string;
  name: string;
  description: string;
  icon: IconType;
  category: ToolCategory;
  Component: React.LazyExoticComponent<React.ComponentType>;
}

export const tools: ToolMeta[] = [
  {
    slug: 'json-csv',
    name: 'JSON ↔ CSV',
    description: 'Convert data between JSON and CSV, with nested keys flattened.',
    icon: FaExchangeAlt,
    category: 'tools',
    Component: lazy(() => import('./impl/JsonCsv')),
  },
  {
    slug: 'base64',
    name: 'Base64',
    description: 'Encode and decode text or URLs to and from Base64.',
    icon: FaLock,
    category: 'tools',
    Component: lazy(() => import('./impl/Base64')),
  },
  {
    slug: 'text-diff',
    name: 'Text Diff',
    description: 'Compare two blocks of text with color-coded differences.',
    icon: FaColumns,
    category: 'tools',
    Component: lazy(() => import('./impl/TextDiff')),
  },
  {
    slug: 'regex-tester',
    name: 'Regex Tester',
    description: 'Test regular expressions live with matches and capture groups.',
    icon: FaAsterisk,
    category: 'tools',
    Component: lazy(() => import('./impl/RegexTester')),
  },
  {
    slug: 'code-formatter',
    name: 'Code Formatter',
    description: 'Format JS, TS, JSON, HTML and CSS with Prettier.',
    icon: FaAlignLeft,
    category: 'tools',
    Component: lazy(() => import('./impl/CodeFormatter')),
  },
  {
    slug: 'lorem-ipsum',
    name: 'Lorem Ipsum',
    description: 'Generate placeholder text by paragraphs, sentences or words.',
    icon: FaParagraph,
    category: 'tools',
    Component: lazy(() => import('./impl/LoremIpsum')),
  },
  {
    slug: 'color-converter',
    name: 'Color Converter',
    description: 'Convert colors between HEX, RGB and HSL with a live preview.',
    icon: FaPalette,
    category: 'tools',
    Component: lazy(() => import('./impl/ColorConverter')),
  },
  {
    slug: 'hash-generator',
    name: 'Hash Generator',
    description: 'Generate MD5, SHA-1, SHA-256 and SHA-512 hashes from text.',
    icon: FaFingerprint,
    category: 'tools',
    Component: lazy(() => import('./impl/HashGenerator')),
  },
  {
    slug: 'number-base',
    name: 'Number Base',
    description: 'Convert numbers between binary, octal, decimal and hex.',
    icon: FaSuperscript,
    category: 'tools',
    Component: lazy(() => import('./impl/NumberBase')),
  },
  {
    slug: 'markdown-preview',
    name: 'Markdown Preview',
    description: 'Write Markdown and preview the rendered HTML side by side.',
    icon: FaMarkdown,
    category: 'tools',
    Component: lazy(() => import('./impl/MarkdownPreview')),
  },
  {
    slug: 'pdf-travail-exceptionnel',
    name: 'PDF → Excel (Travail exceptionnel)',
    description:
      'Extrait par OCR les champs des bons "travail exceptionnel" (ZIP de PDF) vers un tableau Excel.',
    icon: FaFileExcel,
    category: 'custom',
    Component: lazy(() => import('./impl/PdfTravailExceptionnel')),
  },
];

export const getTool = (slug?: string): ToolMeta | undefined =>
  tools.find((t) => t.slug === slug);

// Categories in display order, keeping only those that contain tools.
export const orderedCategories: ToolCategory[] = (['tools', 'custom'] as ToolCategory[]).filter(
  (c) => tools.some((t) => t.category === c)
);

export const toolsInCategory = (c: ToolCategory): ToolMeta[] =>
  tools.filter((t) => t.category === c);
